import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { apiService, formatApiError } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { StatusPill, StatusFilter, fmtDate } from './AdminModeration';

const STATUSES = ['submitted', 'reviewing', 'approved', 'declined', 'withdrawn', 'all'];
const Consent = ({ ok, label }) => <span className={`text-xs px-2 py-0.5 rounded-full ${ok ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>{label}: {ok ? 'yes' : 'no'}</span>;

const StoryDetail = ({ story, onUpdated }) => {
  const [status, setStatus] = useState(story.status === 'withdrawn' ? 'declined' : story.status);
  const [note, setNote] = useState(story.review_note || '');
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  const save = async () => {
    setBusy(true);
    try { await apiService.admin.reviewStory(story.id, { status, review_note: note || null }); toast({ title: 'Story updated' }); onUpdated(); }
    catch (e) { toast({ title: 'Action failed', description: formatApiError(e), variant: 'destructive' }); } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 border-t pt-4 space-y-4 text-sm" data-testid={`story-detail-${story.id}`}>
      <div className="grid sm:grid-cols-3 gap-3">
        <div><p className="text-gray-500">Member</p><p className="font-medium">{story.member_name} <span className="text-gray-400 break-all">{story.email}</span></p><p className="text-xs text-gray-500">{story.location}</p></div>
        <div><p className="text-gray-500">Partner / together since</p><p className="font-medium">{story.partner_first_name || '—'} · {story.together_since || '—'}</p></div>
        <div><p className="text-gray-500">Display name</p><p className="font-medium">{story.display_name.replace(/_/g, ' ')}</p></div>
      </div>
      <div className="flex flex-wrap gap-2"><Consent ok={story.consent_publish} label="publish" /><Consent ok={story.consent_contact} label="contact" /><Consent ok={story.consent_photo} label="photo" /><span className="text-xs text-gray-400">consented {fmtDate(story.consented_at)}</span></div>
      <div><p className="text-gray-500">Story</p><p className="whitespace-pre-wrap bg-gray-50 rounded p-2" data-testid="story-text">{story.story}</p></div>
      <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">Approval records the decision only. Publishing on the landing page is a separate manual step and requires contacting the member first (and the partner, if named).</p>
      {story.status !== 'withdrawn' && (
        <div className="space-y-2">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Review note (internal)" data-testid="story-review-note" />
          <div className="flex flex-wrap gap-2 items-center">
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded-md px-3 py-2 bg-white" data-testid="story-status-select">
              {['submitted', 'reviewing', 'approved', 'declined'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <Button size="sm" onClick={save} disabled={busy} data-testid="story-status-save-btn">{busy ? 'Saving…' : 'Save review'}</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminStories = () => {
  const [filter, setFilter] = useState('submitted');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const load = () => apiService.admin.getStories({ status_filter: filter, limit: 100 }).then((r) => { setData(r.data.data); setError(''); }).catch((e) => setError(formatApiError(e)));
  useEffect(() => { load(); }, [filter]);
  return (
    <div className="space-y-4" data-testid="admin-stories">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <StatusFilter value={filter} onChange={setFilter} options={STATUSES} testId="stories-filter" />
        <div className="flex items-center gap-3 text-sm text-gray-600">{data && <span data-testid="stories-total">{data.total} stor{data.total === 1 ? 'y' : 'ies'}</span>}<Button size="sm" variant="outline" onClick={load} data-testid="stories-refresh"><RefreshCw className="w-4 h-4" /></Button></div>
      </div>
      {error && <p className="text-red-600" data-testid="stories-error">{error}</p>}
      {data && data.stories.length === 0 && <Card className="p-6 text-center text-gray-500" data-testid="stories-empty">No {filter === 'all' ? '' : filter} member stories.</Card>}
      {data?.stories.map((s) => (
        <Card key={s.id} className="p-4" data-testid={`story-row-${s.id}`}>
          <button type="button" className="w-full text-left flex flex-wrap items-center gap-3" onClick={() => setOpenId(openId === s.id ? null : s.id)} data-testid={`story-toggle-${s.id}`}>
            <StatusPill status={s.status} />
            <span className="font-mono text-xs text-gray-500">#{s.id.slice(0, 8)}</span>
            <span className="font-semibold">{s.member_name}</span>
            <span className="text-gray-600 truncate max-w-md">{s.story.slice(0, 80)}{s.story.length > 80 ? '…' : ''}</span>
            <span className="ml-auto text-xs text-gray-400">{fmtDate(s.created_at)}</span>
          </button>
          {openId === s.id && <StoryDetail story={s} onUpdated={load} />}
        </Card>
      ))}
    </div>
  );
};

export default AdminStories;
